import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  makeStyles,
  Box,
  Divider,
} from '@material-ui/core';
import { TableColumn } from '@backstage/core-components';
import {
  ResourceTableDataType,
  CRDCloudService,
  CRDResourceType,
} from './types';

interface SimpleResourceCardProps<T extends ResourceTableDataType> {
  resource: T;
  columns: TableColumn<T>[];
  cloudService: CRDCloudService;
  resourceType: CRDResourceType;
  colorTheme: string;
}

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
  },
  header: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  title: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
    wordBreak: 'break-word',
  },
  identifier: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    fontFamily: 'monospace',
  },
  status: {
    marginBottom: theme.spacing(1),
  },
  content: {
    padding: theme.spacing(2),
    paddingTop: 0,
    flex: 1,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1.5),
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  detailLabel: {
    fontSize: '0.75rem',
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  detailValue: {
    fontSize: '0.875rem',
    marginTop: theme.spacing(0.25),
    wordBreak: 'break-word',
  },
}));

// Helper function to get status color
const getStatusColor = (
  status: string,
): 'default' | 'primary' | 'secondary' => {
  const statusLower = status.toLowerCase();

  if (
    statusLower.includes('stopped') ||
    statusLower.includes('terminated') ||
    statusLower.includes('inactive')
  ) {
    return 'secondary';
  }
  if (
    statusLower.includes('running') ||
    statusLower.includes('active') ||
    statusLower.includes('available')
  ) {
    return 'primary';
  }

  return 'default';
};

// Helper function to render column value
const renderColumnValue = <T extends ResourceTableDataType>(
  resource: T,
  column: TableColumn<T>,
): React.ReactNode => {
  const field = column.field as keyof T | undefined;
  const value = field ? resource[field] : '';

  if (value === null || value === undefined) {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'number') {
    return value.toString();
  }

  return String(value);
};

export const SimpleResourceCard = <T extends ResourceTableDataType>({
  resource,
  columns,
  colorTheme,
}: SimpleResourceCardProps<T>) => {
  const classes = useStyles();

  // Find key fields for header display
  const nameColumn = columns.find(col => col.field === 'name');
  const identifierColumn = columns.find(col => col.field === 'identifier');

  const statusColumn = columns.find(
    col => col.field === 'status' || col.field === 'state',
  );

  // Get header field names to exclude from details
  const headerFields = new Set(
    [nameColumn?.field, identifierColumn?.field, statusColumn?.field].filter(
      Boolean,
    ),
  );

  // Filter columns for details section (exclude header fields)
  const detailColumns = columns.filter(
    col => col.field && !headerFields.has(col.field),
  );

  const name = nameColumn
    ? renderColumnValue(resource, nameColumn)
    : 'Unknown Resource';
  const identifier = identifierColumn
    ? renderColumnValue(resource, identifierColumn)
    : '';
  const status = statusColumn
    ? String(renderColumnValue(resource, statusColumn))
    : '';

  return (
    <Card
      className={classes.card}
      style={{ borderTop: `4px solid ${colorTheme}` }}
    >
      {/* Header */}
      <div className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          {name}
        </Typography>

        {identifier && (
          <Typography variant="body2" className={classes.identifier}>
            {identifier}
          </Typography>
        )}

        {status && (
          <Box className={classes.status}>
            <Chip
              label={status}
              size="small"
              color={getStatusColor(status)}
              variant="outlined"
            />
          </Box>
        )}
      </div>

      <Divider />

      {/* Details */}
      <CardContent className={classes.content}>
        <div className={classes.detailsGrid}>
          {detailColumns.map(column => (
            <div
              key={String(column.field ?? column.title)}
              className={classes.detailItem}
            >
              <Typography variant="caption" className={classes.detailLabel}>
                {column.title}
              </Typography>
              <Typography variant="body2" className={classes.detailValue}>
                {renderColumnValue(resource, column)}
              </Typography>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
